<template>
  <div class="row mr-0">
    <div class="col-12 col-md-8 col-lg-6 col-xl-6">
      <validation-provider
        tag="div"
        :name="$t('form.labels.processorFixed')"
        rules="required|decimal|min_value:0|max_value:100"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.processorFixed"
          :label="$t('form.labels.processorFixed')"
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        tag="div"
        :name="$t('form.labels.processorVariable')"
        rules="required|decimal|min_value:0|max_value:100"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.processorVariable"
          :label="$t('form.labels.processorVariable')"
          required
          :error="errors[0]"
        />
      </validation-provider>

      <template v-if="!isClientGateway">
        <validation-provider
          tag="div"
          :name="$t('form.labels.platformCommissionLevels')"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.enablePlatformCommissionLevels"
            :items="platformCommissionLevels"
            :label="$t('form.labels.platformCommissionLevels')"
            :placeholder="$t('form.placeholders.select')"
            label-key="label"
            value-key="id"
            required
            filterable
            :error="errors[0]"
          />
        </validation-provider>

        <location-platform-commission-levels
          v-if="form.enablePlatformCommissionLevels"
          :items="form.platformCommissionLevels"
          :currency-label="locationCurrency"
          :max-tips-amount="form.maxTipsAmount"
        />
        <validation-provider
          v-else
          tag="div"
          :name="$t('form.labels.platformCommissionPercent')"
          rules="required|decimal|min_value:0|max_value:100"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-input
            v-model="form.feePlan"
            :label="$t('form.labels.platformCommissionPercent')"
            required
            :error="errors[0]"
            :disabled="!form.type"
          />
        </validation-provider>
      </template>
    </div>
    <div class="col-12 col-md-8 col-lg-6 col-xl-6">
      <validation-provider
        tag="div"
        :name="$t('form.labels.maxTipsAmount', { currency: locationCurrency })"
        rules="required|decimal|min_value:1"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.maxTipsAmount"
          :label="$t('form.labels.maxTipsAmount', { currency: locationCurrency })"
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="showIndividualPayoutAndTransferFees"
        tag="div"
        :name="individualPayoutAndTransferFeesLabel"
        rules="required"
        mode="passive"
        class="my-2"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.payoutPlanSetup"
          :items="payoutPlanSetupList"
          :label-info="form.payoutPlanSetup ? form.payoutPlanSetup.toolTipText : $t(`payoutPlanSetup.tooltipExample.${currencyCode}`)"
          :label="individualPayoutAndTransferFeesLabel"
          :placeholder="$t('form.placeholders.select')"
          label-key="name"
          value-key="id"
          value-original
          filterable
          required
          :disabled="!form.country || (Boolean(workplaceId) && !isSupportAdmin)"
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="showCommonPayoutAndTransferFees"
        tag="div"
        :name="commonPayoutAndTransferFeesLabel"
        rules="required"
        mode="passive"
        class="my-2"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.commonPayoutPlanSetup"
          :items="commonPayoutPlanSetupList"
          :label-info="form.commonPayoutPlanSetup ? form.commonPayoutPlanSetup.toolTipText : $t(`payoutPlanSetup.tooltipExample.${currencyCode}`)"
          :label="commonPayoutAndTransferFeesLabel"
          :placeholder="$t('form.placeholders.select')"
          label-key="name"
          value-key="id"
          value-original
          filterable
          required
          :disabled="!form.country || (Boolean(workplaceId) && !isSupportAdmin)"
          :error="errors[0]"
        />
      </validation-provider>

      <div
        v-if="showAllowInstantPayouts"
        class="form-group d-flex align-items-center"
      >
        <ui-switcher
          v-model="form.enableInstantPayouts"
          :label="$t('form.labels.allowInstantPayouts')"
          :label-info="$t('instantPayouts.tooltip')"
        />
      </div>

      <validation-provider
        tag="div"
        :name="$t('form.labels.platformFeesOptions')"
        rules="required"
        mode="passive"
        class="my-2"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.platformFeeType"
          :items="feeTypes"
          :label-info="feeTypesDescription"
          :label="$t('form.labels.platformFeesOptions')"
          :placeholder="$t('form.placeholders.select')"
          label-key="name"
          value-key="type"
          filterable
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="showSplitFees"
        tag="div"
        :name="$t('form.labels.splitFee')"
        rules="required|float|min_value:0"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.feeSplitPercentage"
          :label="$t('form.labels.splitFee')"
          required
          :error="errors[0]"
        />
      </validation-provider>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import getCurrency from '@/utils/getCurrency';
import { LABELS } from '@/config/currencies';
import { PROCESSING_METHOD } from '@/config/processingMethod';
import { PLATFORM_FEE_TYPES } from '@/config/platformFeeTypes';
import { buildCommissionLevels } from '@/config/platformCommissionLevels';
import LocationPlatformCommissionLevels from './LocationPlatformCommissionLevels.vue';

export default {
  name: 'LocationPlatformFees',

  components: {
    LocationPlatformCommissionLevels,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    workplaceId: {
      type: String,
      default: null,
    },
    showIndividualPayoutAndTransferFees: Boolean,
    showCommonPayoutAndTransferFees: Boolean,
    showAllowInstantPayouts: Boolean,
  },

  computed: {
    ...mapState('settings', [
      'feeTypes',
      'payoutPlanSetupList',
      'commonPayoutPlanSetupList',
    ]),
    ...mapGetters({
      isSupportAdmin: 'auth/isSupportAdmin',
    }),
    // Payout plan fees are currency-based, so their example tooltip follows the selected currency.
    currencyCode() {
      if (!this.form.country) {
        return '';
      }

      return (this.form.currency || getCurrency(this.form.country).iso).toUpperCase();
    },
    locationCurrency() {
      if (!this.form.country) {
        return '';
      }

      // Countries may allow more than one currency, so the selected one wins over the country default.
      return LABELS[this.form.currency] || getCurrency(this.form.country).label;
    },
    isClientGateway() {
      return this.form.processingMethod === PROCESSING_METHOD.CLIENT_GATEWAY;
    },
    platformCommissionLevels() {
      return [
        {
          id: false,
          label: this.$t('platformCommissionLevel.disable'),
        },
        {
          id: true,
          label: this.$t('platformCommissionLevel.enable'),
        },
      ];
    },
    showSplitFees() {
      return [
        PLATFORM_FEE_TYPES.CLIENT_PAYS_SPLIT,
        PLATFORM_FEE_TYPES.ASK_CLIENT_TO_PAY_SPLIT,
      ].includes(this.form.platformFeeType);
    },
    individualPayoutAndTransferFeesLabel() {
      return `Payout fees, ${this.locationCurrency}`;
    },
    commonPayoutAndTransferFeesLabel() {
      return `Payout fees for Common Tips, ${this.locationCurrency}`;
    },
    feeTypesDescription() {
      return this.feeTypes.map((item) => item.description).join('<br><br>');
    },
  },

  watch: {
    'form.enablePlatformCommissionLevels': {
      handler(newValue) {
        this.form.platformCommissionLevels = newValue
          ? buildCommissionLevels(this.form.platformCommissionLevels, this.form.maxTipsAmount)
          : [];
      },
    },
    'form.maxTipsAmount': {
      handler(newValue) {
        if (newValue && this.form.enablePlatformCommissionLevels) {
          this.form.platformCommissionLevels = buildCommissionLevels(this.form.platformCommissionLevels, newValue);
        }
      },
    },
  },
};
</script>
